import { useCallback, useEffect, useMemo, useState } from 'react';
import Form, { Validations } from '../../../../../components/Form';
import { ControlItem } from '../../../types';
import axios, { Method } from 'axios';
import { formatParam, getFieldsFromObject } from '../../helpers';
import useRequest from '../../../../../hooks/useRequest';

import ServicePaths from '../../../../../bridge/utils/ServicePaths';

const EditSubscriptionForm = () => {
  const [userId, setUserId] = useState<string>();
  const [botId, setBotId] = useState<string | undefined>();

  const [canFetchSubscription, setCanFetchSubscription] = useState(false);
  const [subscription, setSubscription] = useState<
    Record<string, any> | undefined
  >();

  const [errors, setErrors] = useState<Record<string, Validations>>({});
  const [formMessage, setFormMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const params = useMemo(
    () => ({ userId, botId, clusterId: 'PoC' }),
    [userId, botId],
  );
  const options = useMemo(
    () => ({
      shouldFetch: canFetchSubscription,
      method: 'GET' as Method,
    }),
    [canFetchSubscription],
  );

  const { data: subscriptionData } = useRequest(
    ServicePaths.CONTROL.GetRawSubscription,
    params,
    options,
  );

  useEffect(() => {
    if (!subscriptionData?.subscription) {
      return;
    }

    setSubscription(subscriptionData.subscription);
  }, [subscriptionData]);

  const checkValidation = useCallback(() => {
    /** userId can not be empty */
    let didError;

    setErrors({});

    if (typeof userId === 'undefined') {
      setErrors((prev) => ({
        ...prev,
        userId: Validations.Required,
      }));

      didError = true;
    }

    if (typeof botId === 'undefined') {
      setErrors((prev) => ({
        ...prev,
        botId: Validations.Required,
      }));

      didError = true;
    }

    return !didError;
  }, [botId, userId]);

  const handleGetSubscriptionSubmit = useCallback(() => {
    const canContinue = checkValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    setCanFetchSubscription(true);
  }, [checkValidation]);

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    const canContinue = checkValidation();

    if (!canContinue || !subscription) {
      return;
    }

    const { data } = await axios({
      method: 'POST',
      url: ServicePaths.CONTROL.EditSubscription,
      params: {
        userId: formatParam(userId),
        botId: formatParam(botId),
        subscription: JSON.stringify(subscription),
      },
    });

    setIsLoading(false);
    setFormMessage(data);
  }, [botId, checkValidation, subscription, userId]);

  const subscriptionDataConfig = useMemo(() => {
    return {
      title: ControlItem.EditSubscription,
      formMessage,
      fields: [
        {
          label: 'User Id',
          value: userId,
          handleValueChange: setUserId,
          error: errors.userId,
        },
        {
          label: 'Bot Id',
          value: botId,
          handleValueChange: setBotId,
          error: errors.botId,
        },
      ],
      handleSubmit: handleGetSubscriptionSubmit,
      isLoading,
    };
  }, [
    botId,
    errors.botId,
    errors.userId,
    formMessage,
    handleGetSubscriptionSubmit,
    isLoading,
    userId,
  ]);

  const config = useMemo(() => {
    if (!subscription) {
      return null;
    }

    /** Loop over values */
    const fields = getFieldsFromObject(subscription, errors, setSubscription);

    return {
      title: ControlItem.EditSubscription,
      formMessage,
      fields: fields,
      handleSubmit,
      isLoading,
    };
  }, [errors, formMessage, handleSubmit, isLoading, subscription]);

  if (!subscription) {
    return <Form config={subscriptionDataConfig} />;
  }

  if (!config) {
    return null;
  }

  return <Form config={config} />;
};

export default EditSubscriptionForm;
